/**
 * WarehouseLowStockPanel
 * Fetches /api/warehouse/items/out_of_stock/ and /api/warehouse/items/low_stock/
 * Shows prioritised list: out-of-stock first, then low-stock sorted by remaining %.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, RefreshCw, Loader2, PackageX } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const StatusBadge = ({ status }) => {
  const cfg = {
    out_of_stock: { label: 'Empty',    bg: '#FEE2E2', color: '#EF4444' },
    low_stock:    { label: 'Low',      bg: '#FEF3C7', color: '#D97706' },
    in_stock:     { label: 'OK',       bg: '#D1FAE5', color: '#059669' },
  };
  const c = cfg[status] || cfg.in_stock;
  return (
    <span style={{ backgroundColor: c.bg, color: c.color }}
      className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
      {c.label}
    </span>
  );
};

const StockBar = ({ current, min }) => {
  const max  = Math.max(min * 2, current, 1);
  const pct  = Math.min(Math.round((current / max) * 100), 100);
  const minP = Math.min(Math.round((min / max) * 100), 100);
  const color = current === 0 ? '#EF4444' : current <= min ? '#F59E0B' : '#10B981';
  return (
    <div className="relative h-1.5 bg-steel-100 rounded-full overflow-visible w-20 flex-shrink-0">
      {/* min-stock marker */}
      <div className="absolute top-0 bottom-0 w-px bg-slate-400 z-10"
        style={{ left: `${minP}%` }} />
      {/* current-stock fill */}
      <div className="h-full rounded-full transition-all"
        style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
};

const WarehouseLowStockPanel = () => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    const toArr = r => Array.isArray(r.data) ? r.data : (r.data?.results ?? []);

    Promise.all([
      axios.get(`${API_BASE}/api/warehouse/items/out_of_stock/`, { headers }),
      axios.get(`${API_BASE}/api/warehouse/items/low_stock/`,    { headers }),
    ])
      .then(([oRes, lRes]) => {
        const outOfStock = toArr(oRes).map(i => ({ ...i, _priority: 0 }));
        const lowStock   = toArr(lRes)
          .filter(i => i.quantity_in_stock > 0)   // exclude already in out_of_stock
          .map(i => {
            const pct = i.minimum_stock_level > 0
              ? i.quantity_in_stock / i.minimum_stock_level
              : 1;
            return { ...i, _priority: 1, _pct: pct };
          });

        const merged = [
          ...outOfStock,
          ...lowStock.sort((a, b) => a._pct - b._pct),
        ];
        setItems(merged);
      })
      .catch(() => setError('Could not load stock data.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500">
            <AlertTriangle size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Low Stock Alert</span>
          {!loading && !error && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-500">
              {items.length} items
            </span>
          )}
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <PackageX size={28} />
          <span className="text-xs">All stock levels OK</span>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* column headers */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-steel-100 bg-steel-50 sticky top-0 z-10">
            <span className="flex-1 text-[9px] font-semibold text-slate-400 uppercase tracking-wide">Item</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-20 text-center">Stock</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-12 text-right">Qty</span>
            <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide w-10 text-right">Status</span>
          </div>

          {items.map((item, i) => (
            <div key={item.id}
              className={`flex items-center gap-2 px-3 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
              {/* name */}
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold text-slate-700 truncate">{item.name}</p>
                {item.sku && <p className="text-[9px] text-slate-400">{item.sku}</p>}
              </div>

              {/* stock bar */}
              <StockBar current={item.quantity_in_stock} min={item.minimum_stock_level || 0} />

              {/* qty */}
              <span className="text-[11px] font-bold w-12 text-right" style={{
                color: item.quantity_in_stock === 0 ? '#EF4444' : '#D97706'
              }}>
                {item.quantity_in_stock}
                <span className="text-[9px] font-normal text-slate-400 ml-0.5">{item.unit || 'pcs'}</span>
              </span>

              {/* status */}
              <div className="w-10 flex justify-end">
                <StatusBadge status={item.stock_status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WarehouseLowStockPanel;
